import { DayType } from '@/app/constants/dayType'
import { DriverCalendarModel } from '@/app/models/calendar/driverCalendarModel'
import type { DriverLeaveSummaryModel } from '@/app/models/timesheet/leaveSummaryModel'


export type LeaveCounter = {
  key: 'u' | 'bw' | 'uz' | 'overdue'
  used: number
  limit: number | null
  limitUnknown?: boolean
}

export type LeaveSummary = Array<LeaveCounter>

const ON_DEMAND_LEAVE_LIMIT = 4

export const getLeaveSummary = (
  calendar: DriverCalendarModel | null,
): LeaveSummary | null => {
  if (!calendar?.dayActivities?.length) return null

  const counts = new Map<DayType, number>()

  calendar.dayActivities.flat().forEach((day) => {
    if (!day || day.dayNumber === 0 || day.isHoliday) return
    counts.set(day.dayType, (counts.get(day.dayType) ?? 0) + 1)
  })

  const count = (...dayTypes: Array<DayType>) =>
    dayTypes.reduce((sum, dayType) => sum + (counts.get(dayType) ?? 0), 0)

  return [
    {
      key: 'u',
      used: count(DayType.U, DayType.MergedU, DayType.Uz),
      limit: null,
      limitUnknown: true,
    },
    { key: 'bw', used: count(DayType.Bw), limit: null },
    {
      key: 'uz',
      used: count(DayType.Uz),
      limit: ON_DEMAND_LEAVE_LIMIT,
    },
    {
      key: 'overdue',
      used: count(DayType.U, DayType.MergedU),
      limit: null,
      limitUnknown: true,
    },
  ]
}


export const mergeLeaveSummaryWithApi = (
  summary: LeaveSummary | null,
  api: DriverLeaveSummaryModel | null,
): LeaveSummary | null => {
  if (!summary || !api) return summary

  return summary.map((counter): LeaveCounter => {
    switch (counter.key) {
      case 'u':
        return {
          ...counter,
          used: api.usedLeaveDays,
          limit: api.annualLeaveDays + api.overdueLeaveDays,
          limitUnknown: false,
        }
      case 'uz':
        return {
          ...counter,
          used: api.usedOnDemandLeaveDays,
          limit: api.onDemandLeaveLimit,
        }
      case 'overdue':
        return {
          ...counter,
          used: api.usedOverdueLeaveDays ?? counter.used,
          limit: api.overdueLeaveDays,
          limitUnknown: false,
        }
      default:
        return counter
    }
  })
}
